import { IIndustryWithPolicyTypes, updateIndustry } from "@/api/industries";
import { Button } from "@/components/shared/buttons/Button";
import { Panel } from "@/components/shared/Panel";
import { Edit } from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import { FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface Props {
    industry: IIndustryWithPolicyTypes;
}

interface IIndustryInfoForm {
    name: string;
    naic: string;
}

export const IndustryInfoSection = ({ industry }: Props) => {
    const [isEditing, setIsEditing] = useState(false);
    const queryClient = useQueryClient();

    const { mutate: updateIndustryMutation, isPending: isSaving } = useMutation(
        {
            mutationFn: async (formData: IIndustryInfoForm) => {
                return updateIndustry(industry.id, {
                    name: formData.name.trim(),
                    naic: formData.naic.trim(),
                });
            },
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ["admin-industry", String(industry.id)],
                });
                queryClient.invalidateQueries({
                    queryKey: ["admin-industries"],
                });
                setIsEditing(false);
            },
        }
    );

    const handleSave = (formData: IIndustryInfoForm) => {
        updateIndustryMutation(formData);
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    const defaultValues: IIndustryInfoForm = {
        name: industry.name,
        naic: industry.naic,
    };

    return (
        <Panel
            title="Industry Information"
            actions={
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit fontSize="small" />}
                    onClick={() => setIsEditing(true)}
                    sx={{
                        visibility: isEditing ? "hidden" : "visible",
                        color: (theme) => theme.palette.primary.contrastText,
                        borderColor: (theme) =>
                            theme.palette.primary.contrastText,
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.action.hover,
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                        },
                    }}
                >
                    Edit
                </Button>
            }
        >
            {isEditing ? (
                <FormContainer
                    defaultValues={defaultValues}
                    onSuccess={handleSave}
                >
                    <Stack spacing={2.5}>
                        <FormTextFieldElement
                            label="Name"
                            name="name"
                            size="small"
                            rules={{
                                validate: (value: string) =>
                                    value.trim() !== "" ||
                                    "Name cannot be empty",
                            }}
                            fullWidth
                            required
                        />

                        <FormTextFieldElement
                            label="NAIC Code"
                            name="naic"
                            size="small"
                            rules={{
                                validate: (value: string) =>
                                    value.trim() !== "" ||
                                    "NAIC Code cannot be empty",
                            }}
                            inputProps={{ maxLength: 6 }}
                            fullWidth
                            required
                        />

                        <Stack
                            direction="row"
                            gap={1}
                            justifyContent="flex-end"
                        >
                            <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                size="small"
                                loading={isSaving}
                            >
                                Save
                            </Button>
                        </Stack>
                    </Stack>
                </FormContainer>
            ) : (
                <Stack spacing={2}>
                    <InfoField label="Name" value={industry.name} />
                    <InfoField label="NAIC Code" value={industry.naic} />
                </Stack>
            )}
        </Panel>
    );
};

interface IInfoFieldProps {
    label: string;
    value: string;
}

const InfoField = ({ label, value }: IInfoFieldProps) => (
    <Stack spacing={0.5}>
        <Typography variant="body2" color="textSecondary" fontWeight={500}>
            {label}
        </Typography>
        <Typography variant="body2">{value || "-"}</Typography>
    </Stack>
);
